import type {
  BoundaryMap,
  LiveQueryView,
  Nullable,
  ContentTextPosition as SDKContentTextPosition,
  ContentType as SDKContentType,
  FolderReference as SDKFolderReference,
  ItemStatus as SDKItemStatus,
  LibraryItem as SDKLibraryItem,
  LibraryItem as SDKLibraryItemType,
  Result as SDKResultType,
  SearchResult as SDKSearchResult,
  SpeechifyURI as SDKSpeechifyURI,
  SyncedListeningProgress as SDKSyncedListeningProgress
} from '@speechifyinc/multiplatform-sdk';
import { getAuth } from 'firebase/auth';
import { Timestamp, doc, getFirestore, updateDoc } from 'firebase/firestore';
import { IListeningProgress, IRecord, ItemType, RecordStatus, RecordType } from 'interfaces';

import { ErrorSource } from 'config/constants/errors';
import { logError } from 'lib/observability';
import { LibraryFilterAndSortOptions, LibraryFilterType, LibrarySortBy, LibrarySortOrder } from 'modules/library/constants';
import { WebAppImportFlow, WebAppImportType } from 'modules/library/import/constants';
import { ILibraryItem } from 'modules/library/types/item';
import { guestListeningSessionStoreActions } from 'modules/listening/stores/guestListeningSession/guestListeningSessionStore';
import { getRequiredStringEnv } from 'utils/safeEnvParsing';

import { SDKContentSubType } from '../analytics/contentSubType';
import type { MultiplatformSDKInstance } from '../sdk';
import { SDKFacade } from './_base';
import { ContentMetaType, ListenableContent } from './listenableContent';

function unreachable(value: never) {
  return value;
}

export type LibraryFolderSubscription = {
  hasMoreItems: boolean;
  loadMoreItems: () => Promise<boolean>;
  cleanup: () => void;
  getCurrentItems: () => Promise<ILibraryItem[]>;
};

export type LoadMoreItemsResult = {
  items: ILibraryItem[];
  hasMoreItems: boolean;
  loadMoreItems: () => Promise<LoadMoreItemsResult>;
} | void;

export class SDKLibraryFacade extends SDKFacade {
  private static _singleton: SDKLibraryFacade;

  private itemIdToRawSdkItem: Record<string, SDKLibraryItem> = {};

  constructor(sdk: MultiplatformSDKInstance) {
    super(sdk);
    SDKLibraryFacade._singleton = this;
    SDKLibraryFacade.resolveInstance(SDKLibraryFacade._singleton);
  }

  static override get singleton(): SDKLibraryFacade {
    return SDKLibraryFacade._singleton;
  }

  private static resolveInstance: (instance: SDKLibraryFacade) => void = () => {};
  private static instancePromise = new Promise<SDKLibraryFacade>(resolve => {
    SDKLibraryFacade.resolveInstance = resolve;
  });

  static get waitForInstance(): Promise<SDKLibraryFacade> {
    return SDKLibraryFacade.instancePromise;
  }

  private _originalGetItem = this.sdk.promisify(this.sdk.client.libraryService.getItem.bind(this.sdk.client.libraryService));
  private _originalUpdateItem = this.sdk.promisify(this.sdk.client.libraryService.updateItem.bind(this.sdk.client.libraryService));
  private _originalDeleteItem = this.sdk.promisify(this.sdk.client.libraryService.deleteItem.bind(this.sdk.client.libraryService));
  private _originalArchiveItem = this.sdk.promisify(this.sdk.client.libraryService.archiveItem.bind(this.sdk.client.libraryService));
  private _originalRestoreItem = this.sdk.promisify(this.sdk.client.libraryService.restoreItem.bind(this.sdk.client.libraryService));
  private _originalCreateFolder = this.sdk.promisify(this.sdk.client.libraryService.createFolder.bind(this.sdk.client.libraryService));
  private _originalMoveItem = this.sdk.promisify(this.sdk.client.libraryService.moveItem.bind(this.sdk.client.libraryService));
  private _originalDeleteAllArchivedItems = this.sdk.promisify(this.sdk.client.libraryService.deleteAllArchivedItems.bind(this.sdk.client.libraryService));

  public getItem = async (itemId: string): Promise<SDKLibraryItem> => {
    if (this.itemIdToRawSdkItem[itemId]) {
      return this.itemIdToRawSdkItem[itemId];
    }

    const item = await this._originalGetItem(itemId, null);

    if (item) {
      this.itemIdToRawSdkItem[itemId] = item;
    }

    return item;
  };

  public getChildrenItems = this.sdk.promisify(this.sdk.client.libraryService.getChildrenItems.bind(this.sdk.client.libraryService));

  public getCount = this.sdk.promisify(this.sdk.client.libraryService.getCount.bind(this.sdk.client.libraryService));

  public getRootFolder = this.sdk.promisify(this.sdk.client.libraryService.getRootFolder.bind(this.sdk.client.libraryService));

  public getTopItems = this.sdk.promisify(this.sdk.client.libraryService.getTopItems.bind(this.sdk.client.libraryService));

  public search = this.sdk.promisify(this.sdk.client.libraryService.search.bind(this.sdk.client.libraryService));

  public getTopLevelArchivedItems = this.sdk.promisify(this.sdk.client.libraryService.getTopLevelArchivedItems.bind(this.sdk.client.libraryService));

  public addDefaultLibraryItems = this.sdk.promisify(this.sdk.client.libraryService.addDefaultLibraryItems.bind(this.sdk.client.libraryService));

  public updateItem = async (itemId: string, params: Parameters<typeof this.sdk.client.libraryService.updateItem>[1]) => {
    const result = await this._originalUpdateItem(itemId, params);

    // Update cache after successful update
    if (this.itemIdToRawSdkItem[itemId]) {
      // Refetch the item to get updated version
      const updatedItem = await this._originalGetItem(itemId, null);
      if (updatedItem) {
        this.itemIdToRawSdkItem[itemId] = updatedItem;
      }
    }

    return result;
  };

  public deleteItem = async (itemId: string) => {
    const result = await this._originalDeleteItem(itemId);

    // Remove from cache after deletion
    delete this.itemIdToRawSdkItem[itemId];

    return result;
  };

  public archiveItem = async (itemId: string) => {
    const result = await this._originalArchiveItem(itemId);

    // Update the item in cache after archiving
    if (this.itemIdToRawSdkItem[itemId]) {
      const updatedItem = await this._originalGetItem(itemId, null);
      if (updatedItem) {
        this.itemIdToRawSdkItem[itemId] = updatedItem;
      }
    }

    return result;
  };

  public restoreItem = async (itemId: string) => {
    const result = await this._originalRestoreItem(itemId);

    // Update the item in cache after restoring
    if (this.itemIdToRawSdkItem[itemId]) {
      const updatedItem = await this._originalGetItem(itemId, null);
      if (updatedItem) {
        this.itemIdToRawSdkItem[itemId] = updatedItem;
      }
    }

    return result;
  };

  public updateItemCoverImagePath = async (itemId: string, coverImagePath: string) => {
    const { UpdateLibraryItemParams } = this.sdk.sdkModule;
    await this.updateItem(itemId, new UpdateLibraryItemParams(null, coverImagePath, null));
  };

  public addFolderToRootFolder = async (folderName: string) => {
    const rootFolder = await this.getRootFolder();
    const newFolder = await this.createFolder(folderName, rootFolder);
    return newFolder;
  };

  // TODO(albertusdev): Consolidate this into ContentMetaType from ListenableContent
  private contentTypeToRecordType = (contentType: SDKContentType): RecordType => {
    const { ContentType } = this.sdk.sdkModule;
    switch (contentType) {
      case ContentType.SCAN:
        return RecordType.SCAN;
      case ContentType.EPUB:
        return RecordType.EPUB;
      case ContentType.PDF:
        return RecordType.PDF;
      case ContentType.DOCX:
        return RecordType.DEFAULT;
      case ContentType.HTML:
        return RecordType.WEB;
      case ContentType.TXT:
        return RecordType.TXT;
      default:
        return RecordType.DEFAULT;
    }
  };

  public isRoot = (folderId: string): boolean => {
    return folderId === 'LIBRARY_ROOT_FOLDER_ID';
  };

  private getAnalyticsProperties(analyticsProperties: BoundaryMap<string>) {
    return Object.fromEntries(
      analyticsProperties.entries().map(pair => {
        const key = pair.first;
        const value = pair.second;
        return [key, value];
      })
    );
  }

  public toILibraryItem = (item: SDKLibraryItemType): ILibraryItem => {
    const { LibraryItem, LibraryImportProgress, ItemStatus } = this.sdk.sdkModule;

    // Cache the raw SDK item for future use
    if (item.uri && item.uri.id) {
      this.itemIdToRawSdkItem[item.uri.id] = item;
    }

    const base = {
      id: item.uri.id,
      dateAdded: new Date(item.createdAt),
      dateDeleted: item.removedAt ? new Date(item.removedAt) : undefined
    };
    if (item instanceof LibraryItem.Folder) {
      return {
        ...base,
        coverImagePath: '',
        imgAlt: '',
        title: item.title,
        progress: undefined,
        dateListened: undefined,
        recordType: RecordType.DEFAULT,
        isFolder: true,
        itemsCount: item.childrenCount,
        parentFolderId: this.isRoot(item.parentFolderId) ? '' : item.parentFolderId,
        analyticsProperties: this.getAnalyticsProperties(item.analyticsProperties)
      };
    }
    if (item instanceof LibraryItem.Content) {
      return {
        ...base,
        coverImagePath: item.coverImageUrl || '',
        imgAlt: item.title,
        title: item.title,
        progress: item.listenProgressPercent ? Math.round(item.listenProgressPercent * 100) : undefined,
        dateListened: item.lastListenedAt ? new Date(item.lastListenedAt) : undefined,
        recordType: this.contentTypeToRecordType(item.contentType),
        sourceURL: item.sourceStoredUrl ?? item.sourceUrl ?? '',
        originalSourceURL: item.sourceUrl ?? '',
        wordCount: item.totalWords || 0,
        parentFolderId: this.isRoot(item.parentFolderId) ? '' : item.parentFolderId,
        isError: item.status === ItemStatus.FAILED,
        isProcessing: item.status === ItemStatus.PROCESSING,
        analyticsProperties: this.getAnalyticsProperties(item.analyticsProperties)
      };
    }
    if (item instanceof LibraryItem.DeviceLocalContent) {
      return {
        ...base,
        imgAlt: item.title,
        title: item.title,
        coverImagePath: item.coverImageUrl || '',
        progress: item.libraryImportProgress === LibraryImportProgress.Finished ? 100 : 0,
        dateListened: undefined,
        recordType: item.contentType ? this.contentTypeToRecordType(item.contentType) : RecordType.UNKNOWN,
        isLocalContent: true,
        parentFolderId: this.isRoot(item.parentFolderId) ? '' : item.parentFolderId,
        analyticsProperties: this.getAnalyticsProperties(item.analyticsProperties)
      };
    }
    return {
      ...base,
      coverImagePath: '',
      imgAlt: '',
      title: '',
      progress: undefined,
      dateListened: undefined,
      recordType: RecordType.UNKNOWN,
      isAvailableOffline: false
    };
  };

  public subscribeToFolder = async (
    options: {
      folderId: string;
      foldersOnTop?: boolean;
    } & LibraryFilterAndSortOptions,
    callbacks: {
      onItemsChanged: (items: ILibraryItem[]) => void;
      onHasMoreItemsChanged: (hasMoreItems: boolean) => void;
      onError?: (error: Error) => void;
    }
  ): Promise<LibraryFolderSubscription> => {
    try {
      const { Result } = this.sdk.sdkModule;
      if (!options.foldersOnTop) {
        return this._subscribeToFolderDefault(options, callbacks);
      }

      const cleanupState = {
        isCleanedUp: false
      };

      const { FolderReferenceFactory, FilterAndSortOptions, FilterType } = this.sdk.sdkModule;

      const folderSubscription = await this.getChildrenItems(
        FolderReferenceFactory.getInstance().fromId(options.folderId),
        new FilterAndSortOptions(FilterType.FOLDERS, this.translateSortBy(options.sortBy), this.translateSortOrder(options.sortOrder))
      );

      const filterType = options.filterType === LibraryFilterType.ANY ? LibraryFilterType.RECORDS : options.filterType;

      const fileSubscription = await this.getChildrenItems(
        FolderReferenceFactory.getInstance().fromId(options.folderId),
        new FilterAndSortOptions(this.translateFilterType(filterType), this.translateSortBy(options.sortBy), this.translateSortOrder(options.sortOrder))
      );

      const updateCombinedItems = () => {
        callbacks.onItemsChanged([...folderItems, ...fileItems]);
      };

      let folderItems: ILibraryItem[] = [];
      let fileItems: ILibraryItem[] = [];
      let hasMoreFolders = true;
      let hasMoreFiles = true;

      // Set up the change listener
      const folderDestructor = folderSubscription.addChangeListener(result => {
        if (cleanupState.isCleanedUp) {
          logError(new Error('folderSubscription still active after cleanup'), ErrorSource.LIBRARY);
          return;
        }
        if (result instanceof Result.Success) {
          const view = (result as SDKResultType.Success<LiveQueryView<SDKLibraryItem>>).value;
          view.getCurrentItems(items => {
            folderItems = items.map(this.toILibraryItem);
            updateCombinedItems();
          });
        } else {
          const error = (result as SDKResultType.Failure).error;
          if (callbacks.onError) {
            callbacks.onError(new Error(`Encountered error while subscribing to folder ${options.folderId}: ${error}`));
          }
        }
      });

      const fileDestructor = fileSubscription.addChangeListener(result => {
        if (cleanupState.isCleanedUp) {
          logError(new Error('fileSubscription still active after cleanup'), ErrorSource.LIBRARY);
          return;
        }
        if (result instanceof Result.Success) {
          const view = (result as SDKResultType.Success<LiveQueryView<SDKLibraryItem>>).value;
          view.getCurrentItems(items => {
            fileItems = items.map(this.toILibraryItem);
            updateCombinedItems();
          });
        } else {
          const error = (result as SDKResultType.Failure).error;
          if (callbacks.onError) {
            callbacks.onError(new Error(`Encountered error while subscribing to folder ${options.folderId}: ${error}`));
          }
        }
      });

      const loadMoreItems = async () => {
        if (hasMoreFolders) {
          const folderResult = await this._loadMoreItems(folderSubscription);
          hasMoreFolders = folderResult;
          if (!hasMoreFolders) {
            // Start loading files when no more folders
            const fileResult = await this._loadMoreItems(fileSubscription);
            hasMoreFiles = fileResult;
          }
        } else if (hasMoreFiles) {
          const fileResult = await this._loadMoreItems(fileSubscription);
          hasMoreFiles = fileResult;
        }
        return hasMoreFolders || hasMoreFiles;
      };

      folderSubscription.getCurrentItems(items => {
        folderItems = items.map(this.toILibraryItem);
        fileSubscription.getCurrentItems(items => {
          fileItems = items.map(this.toILibraryItem);
          loadMoreItems().then(hasMoreItems => {
            callbacks.onHasMoreItemsChanged(hasMoreItems);
            updateCombinedItems();
          });
        });
      });

      return {
        hasMoreItems: false,
        cleanup: () => {
          cleanupState.isCleanedUp = true;
          folderDestructor();
          fileDestructor();
          folderSubscription.destroy();
          fileSubscription.destroy();
        },
        loadMoreItems,
        getCurrentItems: async () => {
          return new Promise<ILibraryItem[]>(resolve => {
            folderSubscription.getCurrentItems(items => {
              folderItems = items.map(this.toILibraryItem);
              fileSubscription.getCurrentItems(items => {
                fileItems = items.map(this.toILibraryItem);
                updateCombinedItems();
                resolve([...folderItems, ...fileItems]);
              });
            });
          });
        }
      };
    } catch (error) {
      if (callbacks.onError) {
        callbacks.onError(error as Error);
      }
      logError(new Error(`Encountered error while subscribing to folder ${options.folderId}: ${error}`), ErrorSource.LIBRARY);
      return {
        hasMoreItems: false,
        cleanup: () => {},
        loadMoreItems: () => Promise.resolve(false),
        getCurrentItems: () => Promise.resolve([])
      };
    }
  };

  private async _loadMoreItems(view: LiveQueryView<SDKLibraryItem>): Promise<boolean> {
    const { Result } = this.sdk.sdkModule;
    return new Promise<boolean>(resolve => {
      view.getCurrentItems(previousItems => {
        // Cache all current items
        previousItems.forEach(item => {
          if (item.uri && item.uri.id) {
            this.itemIdToRawSdkItem[item.uri.id] = item;
          }
        });

        view.loadMoreItems(result => {
          if (result instanceof Result.Success) {
            const _view = (result as SDKResultType.Success<LiveQueryView<SDKLibraryItem>>).value;
            _view.getCurrentItems(items => {
              // Cache all new items
              items.forEach(item => {
                if (item.uri && item.uri.id) {
                  this.itemIdToRawSdkItem[item.uri.id] = item;
                }
              });
              resolve(items.length > previousItems.length);
            });
          } else {
            resolve(false);
          }
        });
      });
    });
  }

  private _subscribeToFolderDefault = async (
    options: {
      folderId: string;
    } & LibraryFilterAndSortOptions,
    callbacks: {
      onItemsChanged: (items: ILibraryItem[]) => void;
      onHasMoreItemsChanged: (hasMoreItems: boolean) => void;
      onError?: (error: Error) => void;
    }
  ): Promise<LibraryFolderSubscription> => {
    try {
      const { Result } = this.sdk.sdkModule;

      const cleanupState = {
        isCleanedUp: false
      };
      const { FolderReferenceFactory, FilterAndSortOptions } = this.sdk.sdkModule;
      const view = await this.getChildrenItems(
        FolderReferenceFactory.getInstance().fromId(options.folderId),
        new FilterAndSortOptions(this.translateFilterType(options.filterType), this.translateSortBy(options.sortBy), this.translateSortOrder(options.sortOrder))
      );

      // Set up the change listener
      const destructor = view.addChangeListener(result => {
        if (cleanupState.isCleanedUp) {
          logError(new Error('subscription default view still active after cleanup'), ErrorSource.LIBRARY);
          return;
        }
        if (result instanceof Result.Success) {
          const view = (result as SDKResultType.Success<LiveQueryView<SDKLibraryItem>>).value;
          view.getCurrentItems(items => {
            // Cache items as they come in
            items.forEach(item => {
              if (item.uri?.id) {
                this.itemIdToRawSdkItem[item.uri.id] = item;
              }
            });

            callbacks.onItemsChanged(items.map(this.toILibraryItem));
          });
        } else {
          const error = (result as SDKResultType.Failure).error;
          if (callbacks.onError) {
            callbacks.onError(new Error(`Encountered error while subscribing to folder ${options.folderId}: ${error}`));
          }
        }
      });

      const loadMoreItems = () => {
        return new Promise<boolean>(resolve => {
          view.getCurrentItems(previousItems => {
            view.loadMoreItems(result => {
              if (result instanceof Result.Success) {
                const _view = (result as SDKResultType.Success<LiveQueryView<SDKLibraryItem>>).value;
                _view.getCurrentItems(items => {
                  resolve(items.length > previousItems.length);
                });
              } else {
                resolve(false);
              }
            });
          });
        });
      };

      view.getCurrentItems(items => {
        loadMoreItems().then(hasMoreItems => {
          callbacks.onHasMoreItemsChanged(hasMoreItems);
          callbacks.onItemsChanged(items.map(this.toILibraryItem));
        });
      });

      return {
        hasMoreItems: false,
        cleanup: () => {
          cleanupState.isCleanedUp = true;
          destructor();
          view.destroy();
        },
        loadMoreItems,
        getCurrentItems: () => {
          return new Promise<ILibraryItem[]>(resolve => {
            view.getCurrentItems(items => {
              callbacks.onItemsChanged(items.map(this.toILibraryItem));
              resolve(items.map(this.toILibraryItem));
            });
          });
        }
      };
    } catch (error) {
      if (callbacks.onError) {
        callbacks.onError(error as Error);
      }
      logError(new Error(`Encountered error while subscribing to folder ${options.folderId}: ${error}`), ErrorSource.LIBRARY);
      return {
        hasMoreItems: false,
        cleanup: () => {},
        loadMoreItems: () => Promise.resolve(false),
        getCurrentItems: () => Promise.resolve([])
      };
    }
  };

  public getFolderItems = async (
    options: {
      folderId: string;
      foldersOnTop?: boolean;
    } & LibraryFilterAndSortOptions
  ) => {
    const { FolderReferenceFactory, FilterAndSortOptions, FilterType } = this.sdk.sdkModule;
    if (!options.foldersOnTop) {
      const view = await this.getChildrenItems(
        FolderReferenceFactory.getInstance().fromId(options.folderId),
        new FilterAndSortOptions(this.translateFilterType(options.filterType), this.translateSortBy(options.sortBy), this.translateSortOrder(options.sortOrder))
      );
      return new Promise<ILibraryItem[]>(resolve => {
        view.getCurrentItems(items => {
          view.destroy();
          resolve(items.map(this.toILibraryItem));
        });
      });
    } else {
      const folderView = await this.getChildrenItems(
        FolderReferenceFactory.getInstance().fromId(options.folderId),
        new FilterAndSortOptions(FilterType.FOLDERS, this.translateSortBy(options.sortBy), this.translateSortOrder(options.sortOrder))
      );
      const filterType = options.filterType === LibraryFilterType.ANY ? LibraryFilterType.RECORDS : options.filterType;
      const fileView = await this.getChildrenItems(
        FolderReferenceFactory.getInstance().fromId(options.folderId),
        new FilterAndSortOptions(this.translateFilterType(filterType), this.translateSortBy(options.sortBy), this.translateSortOrder(options.sortOrder))
      );
      let folderItems: ILibraryItem[] = [];
      let fileItems: ILibraryItem[] = [];
      return new Promise<ILibraryItem[]>(resolve => {
        folderView.getCurrentItems(items => {
          folderItems = items.map(this.toILibraryItem);
          fileView.getCurrentItems(items => {
            fileItems = items.map(this.toILibraryItem);
            folderView.destroy();
            fileView.destroy();
            resolve([...folderItems, ...fileItems]);
          });
        });
      });
    }
  };

  public async shareItem(itemId: string): Promise<string> {
    const item = await this.getItem(itemId);
    if (!item) {
      throw new Error('Item not found');
    }

    const db = getFirestore();

    try {
      await updateDoc(doc(db, 'items', itemId), {
        isShared: true
      });

      return `${window.location.origin}/share/${itemId}`;
    } catch (error) {
      logError(error as Error, ErrorSource.LIBRARY);
      throw new Error('Failed to share item');
    }
  }

  public getItemAndWaitUntilListenable = async (itemId: string): Promise<SDKLibraryItem> => {
    const SDKContent = this.sdk.sdkModule.LibraryItem.Content;

    // First try to get the item from the cache
    let item = this.itemIdToRawSdkItem[itemId];

    // If not in cache, fetch it
    if (!item) {
      item = await this.getItem(itemId);
    }

    if (item instanceof SDKContent) {
      if (!item.isInListenableState) {
        await new Promise<void>(resolve => {
          const interval = setInterval(async () => {
            // Always fetch the latest version when checking listenable state
            const updatedItem = (await this.getItem(itemId)) as SDKLibraryItem.Content;
            if (updatedItem.isInListenableState) {
              clearInterval(interval);
              resolve();
            }
          }, 1000);
        });

        // Fetch one more time to get the final listenable state
        item = await this.getItem(itemId);
      }
      return item;
    }
    return item;
  };

  public makeAvailableOffline = async (uri: SDKSpeechifyURI) => {
    return this.sdk.client.offlineAvailabilityManager.makeLibraryItemAvailableOffline(uri, null);
  };

  // ContentSubType is actually a string, but for easier and consolidated tracking we created enums here.
  public webAppImportFlowToSDKContentSubType = (importFlow: WebAppImportType) => {
    switch (importFlow) {
      case WebAppImportType.FILE_UPLOAD:
        return SDKContentSubType.LOCAL_FILES;
      case WebAppImportType.WEB_LINK:
        return SDKContentSubType.WEB_LINK;
      case WebAppImportType.TEXT:
        return SDKContentSubType.TYPE_OR_PASTE_TEXT;
      case WebAppImportType.GOOGLE_DRIVE:
        return SDKContentSubType.GOOGLE_DRIVE;
      case WebAppImportType.DROPBOX:
        return SDKContentSubType.DROPBOX;
      case WebAppImportType.ONE_DRIVE:
        return SDKContentSubType.ONE_DRIVE;
      case WebAppImportType.CANVAS:
        return SDKContentSubType.CANVAS;
      case WebAppImportType.AI_STORY:
        return SDKContentSubType.AI_STORY;
      default:
        return unreachable(importFlow);
    }
  };

  public listenableContentToSDKContentType = (listenableContent: ListenableContent) => {
    const metaType = listenableContent.metaType;
    const { ContentType } = this.sdk.sdkModule;

    switch (metaType) {
      case ContentMetaType.PDF:
        return ContentType.PDF;

      case ContentMetaType.DOCX:
        return ContentType.DOCX;

      case ContentMetaType.TXT:
        return ContentType.TXT;

      case ContentMetaType.HTML:
        return ContentType.HTML;

      case ContentMetaType.EPUB:
        return ContentType.EPUB;

      case ContentMetaType.SCAN:
        return ContentType.SCAN;

      case ContentMetaType.UNKNOWN:
        logError(
          new Error(
            `webAppImportFlowToSDKContentType error: Falling back to SDK ContentType.TXT due to unknown meta type for listenableContent: ${listenableContent.constructor.name}`
          ),
          ErrorSource.FILE_IMPORT
        );
        return ContentType.TXT;
      default:
        return unreachable(metaType);
    }
  };

  public webAppImportFlowToSDKImportFlow = (webAppImportFlow: WebAppImportFlow) => {
    const { ImportFlow } = this.sdk.sdkModule;
    switch (webAppImportFlow) {
      case WebAppImportFlow.PLUS_BUTTON_MODAL:
        return ImportFlow.PlusButtonModal;
      case WebAppImportFlow.PLUS_BUTTON_MODAL_DRAG_AND_DROP:
        return ImportFlow.PlusButtonModalDragAndDrop;
      case WebAppImportFlow.PLUS_BUTTON_MODAL_SELECT_FILES:
        return ImportFlow.PlusButtonModalSelectFiles;
      case WebAppImportFlow.LIBRARY_DRAG_AND_DROP:
        return ImportFlow.LibraryDragAndDrop;
      case WebAppImportFlow.LIBRARY_SELECT_FILES:
        return ImportFlow.LibrarySelectFiles;
      case WebAppImportFlow.LIBRARY_SCREEN_SUGGESTIONS:
        return ImportFlow.LibraryScreenSuggestions;
      case WebAppImportFlow.PILL_PLAYER:
        return ImportFlow.PillPlayer;
      case WebAppImportFlow.LISTENING_SCREEN:
        return ImportFlow.ListeningScreen;
      default:
        return unreachable(webAppImportFlow);
    }
  };

  public searchItems = async (query: string): Promise<LoadMoreItemsResult> => {
    if (!query) return;
    const { SearchRequest, FilterType } = this.sdk.sdkModule;

    const bindLoadMoreItems = async (searchResults: SDKSearchResult) => {
      try {
        const result = await this.sdk.promisify(searchResults.loadMoreItems.bind(searchResults))();

        // Cache search results
        result.items.forEach(item => {
          if (item.uri?.id) {
            this.itemIdToRawSdkItem[item.uri.id] = item;
          }
        });

        return {
          items: result.items.map(this.toILibraryItem),
          hasMoreItems: result.hasMore,
          loadMoreItems: () => bindLoadMoreItems(result)
        };
      } catch (error) {
        logError(error as Error, ErrorSource.LIBRARY);
        return;
      }
    };

    try {
      const searchResults = await this.search(new SearchRequest(query, [FilterType.RECORDS.Companion.all()]));

      // Cache initial search results
      searchResults.items.forEach(item => {
        if (item.uri?.id) {
          this.itemIdToRawSdkItem[item.uri.id] = item;
        }
      });

      return {
        items: searchResults.items.map(this.toILibraryItem),
        hasMoreItems: searchResults.hasMore,
        loadMoreItems: () => bindLoadMoreItems(searchResults)
      };
    } catch (error) {
      logError(error as Error, ErrorSource.LIBRARY);
      return;
    }
  };

  public getLibraryFolders = async (folderId: string): Promise<ILibraryItem[]> => {
    const { FolderReferenceFactory, FilterAndSortOptions, FilterType, SortOrder, SortBy } = this.sdk.sdkModule;
    const view = await this.getChildrenItems(
      FolderReferenceFactory.getInstance().fromId(folderId),
      new FilterAndSortOptions(FilterType.FOLDERS, SortBy.ALPHABETICAL, SortOrder.ASC)
    );

    try {
      let hasMore = true;
      let attempts = 0;
      const MAX_ATTEMPTS = 10; //Adding this to prevent infinite loop in case of a bug with _loadMoreItems

      while (hasMore && attempts < MAX_ATTEMPTS) {
        hasMore = await this._loadMoreItems(view);
        attempts++;
      }

      return new Promise<ILibraryItem[]>(resolve => {
        view.getCurrentItems(items => {
          resolve(items.map(this.toILibraryItem));
        });
      });
    } catch (error) {
      logError(new Error(`Error loading all folders: ${error}`), ErrorSource.LIBRARY);
      return [];
    } finally {
      view.destroy();
    }
  };

  public fetchTotalNumbersOfRecordItemsOwnedByCurrentUser = async () => {
    const { FilterType } = this.sdk.sdkModule;
    const count = await this.getCount(FilterType.RECORDS.Companion.all());
    return count;
  };

  public fetchTotalNumbersOfPdfItemsOwnedByCurrentUser = async () => {
    const { FilterType, RecordType } = this.sdk.sdkModule;
    const count = await this.getCount(FilterType.RECORDS.Companion.ofTypes([RecordType.PDF]));
    return count;
  };

  private translateSortBy(sortBy: LibrarySortBy) {
    const { SortBy } = this.sdk.sdkModule;
    switch (sortBy) {
      case LibrarySortBy.DATE_ADDED:
        return SortBy.DATE_ADDED;
      case LibrarySortBy.ALPHABETICAL:
        return SortBy.ALPHABETICAL;
      case LibrarySortBy.RECENTLY_LISTENED:
        return SortBy.RECENTLY_LISTENED;
      default:
        return unreachable(sortBy);
    }
  }

  private translateSortOrder(sortOrder: LibrarySortOrder) {
    const { SortOrder } = this.sdk.sdkModule;
    switch (sortOrder) {
      case LibrarySortOrder.ASC:
        return SortOrder.ASC;
      case LibrarySortOrder.DESC:
        return SortOrder.DESC;
      default:
        return unreachable(sortOrder);
    }
  }

  private translateFilterType(filterType: LibraryFilterType) {
    const { RecordType, FilterType } = this.sdk.sdkModule;
    switch (filterType) {
      case LibraryFilterType.ANY:
        return FilterType.ANY;
      case LibraryFilterType.RECORDS:
        return FilterType.RECORDS.Companion.all();
      case LibraryFilterType.FOLDERS:
        return FilterType.FOLDERS;
      case LibraryFilterType.TEXT:
        return FilterType.RECORDS.Companion.ofTypes([RecordType.TXT]);
      case LibraryFilterType.PDF:
        return FilterType.RECORDS.Companion.ofTypes([RecordType.PDF]);
      case LibraryFilterType.EPUB:
        return FilterType.RECORDS.Companion.ofTypes([RecordType.EPUB]);
      case LibraryFilterType.WEB_LINKS:
        return FilterType.RECORDS.Companion.ofTypes([RecordType.WEB]);
      case LibraryFilterType.SCANS:
        return FilterType.RECORDS.Companion.ofTypes([RecordType.SCAN]);
      default:
        return unreachable(filterType);
    }
  }

  public createFolder = async (folderName: string, parentFolder: Parameters<typeof this.sdk.client.libraryService.createFolder>[1]) => {
    // Call the original method to create the folder
    const newFolder = await this._originalCreateFolder(folderName, parentFolder);

    // For now, we'll skip caching the folder due to type issues
    // When a folder is accessed via getItem, it will be cached properly

    const folderId = newFolder.asRaw();
    this.getItem(folderId); // trigger cache get

    return newFolder;
  };

  public moveItem = async (itemId: string, newParentFolderId: string) => {
    // Use the FolderReferenceFactory to create a folder reference from the ID
    const { FolderReferenceFactory } = this.sdk.sdkModule;
    const newParentFolder = FolderReferenceFactory.getInstance().fromId(newParentFolderId);

    const result = await this._originalMoveItem(itemId, newParentFolder);

    // Update item in cache after moving
    if (this.itemIdToRawSdkItem[itemId]) {
      const updatedItem = await this._originalGetItem(itemId, null);
      if (updatedItem) {
        this.itemIdToRawSdkItem[itemId] = updatedItem;
      }
    }

    return result;
  };

  public deleteAllArchivedItems = async () => {
    const result = await this._originalDeleteAllArchivedItems();

    // Clear cached items that are archived
    for (const itemId in this.itemIdToRawSdkItem) {
      const item = this.itemIdToRawSdkItem[itemId];
      if (item.removedAt) {
        delete this.itemIdToRawSdkItem[itemId];
      }
    }

    return result;
  };

  public async getPaginatedDeletedItems(): Promise<{ items: ILibraryItem[]; loadMore: () => Promise<ILibraryItem[] | null> } | null> {
    try {
      const view = await this.getTopLevelArchivedItems();

      if (!view) {
        return null;
      }

      // Get initial items
      const items = await this.sdk.promisify(view.getCurrentItems.bind(view))();

      // Cache items
      items.forEach(item => {
        if (item.uri?.id) {
          this.itemIdToRawSdkItem[item.uri.id] = item;
        }
      });

      const libraryItems = items.map(this.toILibraryItem);
      const filteredItems = libraryItems.filter(({ isError }) => !isError);

      // Create loadMore function that returns new items after loading more
      const loadMore = async (): Promise<ILibraryItem[] | null> => {
        try {
          const result = await this.sdk.promisify(view.loadMoreItems.bind(view))();

          if (!result) {
            return null;
          }

          const newItems = await this.sdk.promisify(view.getCurrentItems.bind(view))();

          // Cache new items
          newItems.forEach(item => {
            if (item.uri?.id) {
              this.itemIdToRawSdkItem[item.uri.id] = item;
            }
          });

          const newLibraryItems = newItems.map(this.toILibraryItem);
          return newLibraryItems.filter(({ isError }) => !isError);
        } catch (error) {
          logError(error as Error, ErrorSource.LIBRARY);
          return null;
        }
      };

      return {
        items: filteredItems,
        loadMore
      };
    } catch (error) {
      logError(error as Error, ErrorSource.LIBRARY);
      return null;
    }
  }

  public async getAllDeletedItemsCount(): Promise<number> {
    try {
      let liveQueryView = await this.getTopLevelArchivedItems();
      let itemsCount = 0;

      while (liveQueryView) {
        // Get current items and count them
        const items = await this.sdk.promisify(liveQueryView.getCurrentItems.bind(liveQueryView))();
        const filteredItems = items.map(this.toILibraryItem).filter(item => !item.isError);
        const newItemsCount = filteredItems.length;

        // If count didn't change, we've loaded all items
        if (newItemsCount === itemsCount) {
          break;
        }

        itemsCount = newItemsCount;

        // Try to load more items
        const newLiveQueryView = await this.sdk.promisify(liveQueryView.loadMoreItems.bind(liveQueryView))();
        if (!newLiveQueryView) {
          break;
        }

        liveQueryView = newLiveQueryView;
      }

      return itemsCount;
    } catch (error) {
      logError(error as Error, ErrorSource.LIBRARY);
      return 0;
    }
  }

  // --- Start: Private Helper Methods for libraryItemToIRecord ---

  private _itemContentTypeToRecordType(contentType: SDKContentType): RecordType {
    const { ContentType } = this.sdk.sdkModule;
    switch (contentType) {
      case ContentType.DOCX:
        return RecordType.FILE;
      case ContentType.HTML:
        return RecordType.WEB;
      case ContentType.PDF:
        return RecordType.PDF;
      case ContentType.SCAN:
        return RecordType.SCAN;
      case ContentType.TXT:
        return RecordType.TXT;
      case ContentType.EPUB:
        return RecordType.EPUB;
    }
    return RecordType.DEFAULT;
  }

  private _itemStatusToRecordStatus(itemStatus: SDKItemStatus): RecordStatus {
    const { ItemStatus } = this.sdk.sdkModule;
    switch (itemStatus) {
      case ItemStatus.DONE:
        return RecordStatus.Success;
      case ItemStatus.FAILED:
        return RecordStatus.Failed;
      case ItemStatus.PROCESSING:
        return RecordStatus.Processing;
      default:
        return RecordStatus.Failed;
    }
  }

  private _parseListeningProgress(progress: Nullable<SDKSyncedListeningProgress>): IListeningProgress | null {
    if (!progress) return null;

    const cursor = progress.cursor as SDKContentTextPosition;
    const characterIndex = cursor?.characterIndex || 0;
    const fraction = progress.fraction || 0;
    const timestamp = progress.timestamp || '';

    return {
      cursor: {
        characterIndex
      },
      fraction,
      timestamp
    };
  }

  // --- End: Private Helper Methods ---

  // --- Start: parseUnserializables and parseTimestamp ---
  private _parseTimestamp(timestamp: Timestamp) {
    return Object.entries(timestamp).reduce((accum, [name, value]) => {
      return { ...accum, [`_${name}`]: value };
    }, {});
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _parseUnserializables(obj: Record<string, any>) {
    let continueParsingDates = true;

    const newObj = JSON.parse(JSON.stringify({ ...obj }));

    if (newObj.createdAt && newObj.createdAt.seconds && newObj.createdAt.nanoseconds) {
      if (!newObj.createdAt._seconds) {
        newObj.createdAt._seconds = newObj.createdAt.seconds;
      }

      if (!newObj.createdAt._nanoseconds) {
        newObj.createdAt._nanoseconds = newObj.createdAt.nanoseconds;
      }
    }

    if (newObj.updatedAt && newObj.updatedAt.seconds && newObj.updatedAt.nanoseconds) {
      if (!newObj.updatedAt._seconds) {
        newObj.updatedAt._seconds = newObj.updatedAt.seconds;
      }

      if (!newObj.updatedAt._nanoseconds) {
        newObj.updatedAt._nanoseconds = newObj.updatedAt.nanoseconds;
      }
    }

    if (newObj.createdAt && newObj.createdAt._seconds && newObj.createdAt._nanoseconds) {
      if (newObj.updatedAt && newObj.updatedAt._seconds && newObj.updatedAt._nanoseconds) {
        continueParsingDates = false;
      }
    }

    if (continueParsingDates) {
      if (newObj.createdAt && newObj.createdAt instanceof Timestamp) newObj.createdAt = this._parseTimestamp(newObj.createdAt);
      if (newObj.updatedAt && newObj.updatedAt instanceof Timestamp) newObj.updatedAt = this._parseTimestamp(newObj.updatedAt);

      if (newObj.createdAt && !(newObj.createdAt instanceof Timestamp)) newObj.createdAt = this._parseTimestamp(Timestamp.fromDate(new Date(newObj.createdAt)));

      if (newObj.updatedAt && !(newObj.updatedAt instanceof Timestamp)) newObj.updatedAt = this._parseTimestamp(Timestamp.fromDate(new Date(newObj.updatedAt)));
    }

    return newObj;
  }
  // --- End: parseUnserializables and parseTimestamp ---

  // --- Start: libraryItemToIRecord ---
  public libraryItemToIRecord = (
    libraryItem: SDKLibraryItemType,
    options: { rootFolder?: SDKFolderReference; parentFolderId?: string | null } = {}
  ): IRecord => {
    const { FolderReferenceFactory, LibraryItem } = this.sdk.sdkModule;
    const isParentRootFolder = !!options.rootFolder && options.rootFolder.equals(FolderReferenceFactory.getInstance().fromId(libraryItem.parentFolderId));

    const itemParentFolderId = isParentRootFolder ? null : libraryItem.parentFolderId;
    const parentFolderId = options.parentFolderId !== undefined ? options.parentFolderId : itemParentFolderId;

    const analyticsProperties = this.getAnalyticsProperties(libraryItem.analyticsProperties);

    if (libraryItem instanceof LibraryItem.Folder) {
      const { childrenCount, coverImageUrl, createdAt, id, ownerId, title, updatedAt } = libraryItem as SDKLibraryItemType.Folder;

      return this._parseUnserializables({
        childrenCount,
        coverImagePath: coverImageUrl,
        createdAt: new Date(createdAt),
        id,
        isSDK: true,
        owner: ownerId,
        parentFolderId,
        title,
        type: ItemType.Folder,
        updatedAt: new Date(updatedAt),
        analyticsProperties
      }) as IRecord;
    }

    let contentObject;
    const isLocalContent = libraryItem instanceof LibraryItem.DeviceLocalContent;

    if (isLocalContent) {
      const { contentType, coverImageUrl, createdAt, ownerId, listenProgressStatus, libraryImportProgress, sourceUrl, status, title, updatedAt } =
        libraryItem as SDKLibraryItemType.DeviceLocalContent;

      contentObject = {
        coverImagePath: coverImageUrl,
        createdAt: new Date(createdAt),
        id: libraryItem.uri.id,
        isSDK: true,
        libraryImportProgress,
        owner: ownerId,
        parentFolderId,
        listenProgressStatus: listenProgressStatus?.name,
        recordType: this._itemContentTypeToRecordType(contentType!),
        sourceURL: sourceUrl,
        status: this._itemStatusToRecordStatus(status),
        title,
        type: ItemType.Record,
        updatedAt: new Date(updatedAt),
        analyticsProperties
      };
    } else {
      const {
        contentType,
        coverImageUrl,
        createdAt,
        excerpt,
        id,
        listeningProgress,
        ownerId,
        listenProgressStatus,
        listenProgressPercent,
        libraryImportProgress,
        sourceUrl,
        sourceStoredUrl,
        status,
        title,
        totalWords,
        updatedAt
      } = libraryItem as SDKLibraryItemType.Content;

      contentObject = {
        coverImagePath: coverImageUrl,
        createdAt: new Date(createdAt),
        excerpt,
        id,
        isSDK: true,
        listeningProgress: this._parseListeningProgress(listeningProgress),
        owner: ownerId,
        parentFolderId,
        listenProgressStatus: listenProgressStatus?.name,
        progressPercent: listenProgressPercent ? Math.round(listenProgressPercent * 100) : 0,
        libraryImportProgress,
        recordType: this._itemContentTypeToRecordType(contentType),
        sourceURL: sourceStoredUrl ?? sourceUrl,
        originalSourceURL: sourceUrl,
        status: this._itemStatusToRecordStatus(status),
        title,
        type: ItemType.Record,
        updatedAt: new Date(updatedAt),
        wordCount: totalWords,
        analyticsProperties
      };
    }

    return this._parseUnserializables(contentObject) as IRecord;
  };
  // --- End: libraryItemToIRecord ---

  // --- Start: importSharedItemForUser ---
  public importSharedItemForUser = async (sharedItemId: string, userId: string, newItemId: string): Promise<string> => {
    const importPromise = new Promise<string>((resolve, reject) => {
      getAuth()
        .currentUser?.getIdToken()
        .then(idToken => idToken || '')
        .then(idToken => {
          return fetch(`https://us-central1-${getRequiredStringEnv('NEXT_PUBLIC_FB_PROJECT_ID')}.cloudfunctions.net/copyLibraryItemV2`, {
            method: 'post',
            headers: { 'Content-type': 'application/json; charset=UTF-8', Authorization: `${idToken}` },
            body: JSON.stringify({
              currentRecordUid: sharedItemId,
              userId,
              newRecordUid: newItemId
            })
          });
        })
        .then(response => response.json())
        .then(result => result.message)
        .then(resolve)
        .catch(reject);
    });

    // Assuming guestListeningSessionStoreActions is globally accessible or imported appropriately
    guestListeningSessionStoreActions.setSharedItemImportPromise(importPromise);

    return importPromise;
  };
}
